import type { OverviewCategory } from '../../../services/dmMonitoringOverview';
import type { HistoryBucket } from '../../../services/dmMonitoringOverviewHistory';
import type { GranularityOption } from '../../accounts/GranularityToggle';
import type { ReportingPeriod } from './overviewPeriod';
import { bucketLabel } from './overviewSeries.ts';

export const BUCKET_OPTIONS: GranularityOption<HistoryBucket>[] = [
    { key: 'day', label: 'Day' },
    { key: 'week', label: 'Week' },
    { key: 'month', label: 'Month' }
];

const BUCKET_WINDOWS: Readonly<Record<HistoryBucket, ReportingPeriod>> = {
    day: { label: 'Last 30 days', phrase: 'in the last 30 days' },
    week: { label: 'Last 12 weeks', phrase: 'in the last 12 weeks' },
    month: { label: 'Last 12 months', phrase: 'in the last 12 months' }
};

export const CATEGORY_FIELD = {
    notification: 'notifications',
    message: 'messages'
} as const satisfies Record<OverviewCategory, string>;

export interface ChartSeries {
    key: string;
    label: string;
    color: string;
}

export interface BucketCounts {
    bucketStart: string;
    total: number;
    counts: Record<string, number>;
}

export interface OverTimeRow extends BucketCounts {
    label: string;
    // One value per segment, in segment order.
    segments: number[];
    line?: number;
}

// 'count' lists the largest first, ties in series order; 'series' keeps series order.
export type TooltipOrder = 'count' | 'series';

export interface OverTimeChartData {
    // Tooltip lines, looked up in each row's counts by key.
    series: ChartSeries[];
    // Bars and legend; may group several series, e.g. into Others.
    segments: ChartSeries[];
    rows: OverTimeRow[];
    tooltipOrder: TooltipOrder;
    // Names each row's total in the tooltip.
    totalLabel: string;
    // Drawn over the bars on the same axis, from each row's line value.
    line?: Pick<ChartSeries, 'label' | 'color'>;
}

export const TOTAL_LABEL = 'Total';

export interface TooltipLine extends ChartSeries {
    count: number;
}

export function bucketWindow(bucket: HistoryBucket): ReportingPeriod {
    return BUCKET_WINDOWS[bucket];
}

export function chartRow(
    point: BucketCounts,
    bucket: HistoryBucket,
    segments: number[]
): OverTimeRow {
    return { ...point, label: bucketLabel(point.bucketStart, bucket), segments };
}

// Series and segments are the same fixed list, so the tooltip keeps their order.
export function buildFixedSeriesChart(
    buckets: readonly BucketCounts[],
    bucket: HistoryBucket,
    series: ChartSeries[]
): OverTimeChartData {
    return {
        series,
        segments: series,
        rows: buckets.map((point) =>
            chartRow(
                point,
                bucket,
                series.map(({ key }) => point.counts[key] ?? 0)
            )
        ),
        tooltipOrder: 'series',
        totalLabel: TOTAL_LABEL
    };
}

export function hasCounts(chart: OverTimeChartData): boolean {
    return chart.rows.some((row) => (row.line ?? 0) > 0 || row.segments.some((count) => count > 0));
}

// Anything with none in the bucket is left out.
export function tooltipLines(row: OverTimeRow, chart: OverTimeChartData): TooltipLine[] {
    const lines = chart.series
        .map((series) => ({ ...series, count: row.counts[series.key] ?? 0 }))
        .filter((line) => line.count > 0);
    return chart.tooltipOrder === 'count' ? lines.sort((a, b) => b.count - a.count) : lines;
}
